import React, { useMemo, useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Badge,
  ListGroup,
  InputGroup,
  Spinner,
  ProgressBar,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchCreditqAgents } from "../../store/slices/smartConnect/creditqAgents";
import { getSmartConnectSettings } from "../../store/slices/smartConnect/smartConnectSettings";
import CreateSmartConnectSettingComponent from "./SmartConnectSetting/CreateSmartConnectSetting";
import { toast } from "react-toastify";
import { createDynamicAgentGroupDispatcher } from "../../store/slices/smartConnect/dynamicAgentGroup";

function CreateNewAgentGroup({ show, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const [pendingSettingId, setPendingSettingId] = useState(null);
  const [step, setStep] = useState(1);
  const [groupName, setGroupName] = useState("");
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedSetting, setSelectedSetting] = useState("");
  const [search, setSearch] = useState("");
  const [activeDepartment, setActiveDepartment] = useState("");
  const [showCreateSettingModal, setShowCreateSettingModal] = useState(false);

  const { data: agents = [], loading = false } = useSelector(
    (store) => store.creditqAgentsReducer,
  );

  const {
    data: smartConnectSettings = [],
    loading: smartConnectLoading = false,
  } = useSelector((store) => store.smartConnectSettingsReducer);

  console.log("smartConnectSettings : ", smartConnectSettings);

  const handleSettingCreated = async (newSetting) => {
    if (!newSetting?.id) return;

    setPendingSettingId(newSetting.id);
    dispatch(getSmartConnectSettings());
  };

  useEffect(() => {
    if (
      pendingSettingId &&
      Array.isArray(smartConnectSettings) &&
      smartConnectSettings.some((s) => s.id === pendingSettingId)
    ) {
      setSelectedSetting(pendingSettingId);
      setPendingSettingId(null);
    }
  }, [smartConnectSettings, pendingSettingId]);

  const filteredAgents = useMemo(() => {
    if (!search) return agents;
    const q = search.toLowerCase();
    return agents.filter(
      (a) =>
        a.agent_name?.toLowerCase().includes(q) ||
        a.agent_email?.toLowerCase().includes(q),
    );
  }, [agents, search]);

  const fetchAgents = async (department) => {
    try {
      if (loading) return;

      setActiveDepartment(department);
      setSearch(""); // UX clean

      await dispatch(
        fetchCreditqAgents({
          agentType: department,
          status: "ACTIVE",
        }),
      ).unwrap();
    } catch (err) {
      toast.error(err);
    }
  };

  useEffect(() => {
    if (step === 2) {
      dispatch(getSmartConnectSettings());
    }
  }, [step, dispatch]);

  const toggleAgent = (agent) => {
    setSelectedAgents((prev) => {
      const exists = prev.some((a) => a.id === agent.id);
      if (exists) {
        return prev.filter((a) => a.id !== agent.id);
      }
      return [...prev, agent];
    });
  };

  const removeAgent = (id) => {
    setSelectedAgents((prev) => prev.filter((a) => a.id !== id));
  };

  const handleSubmit = async () => {
    if (!groupName.trim()) {
      toast.error("Group name is required");
      return;
    }

    if (!selectedSetting) {
      toast.error("Please select a Smart Connect setting");
      return;
    }

    if (!Array.isArray(selectedAgents) || selectedAgents.length === 0) {
      toast.error("Please select at least one agent");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      toast.error("User not found. Please re-login.");
      return;
    }

    const userData = user?.data || {};
    if (!userData || !userData?.id) {
      toast.error("User Id Not found. Please Re-login and Try Again Later.");
      return;
    }

    console.log("agents : ", agents);

    const payload = {
      group_name: groupName.trim(),
      setting_id: selectedSetting,
      agents: selectedAgents.map((a) => ({
        cq_agent_id: a.cq_id,
        smart_connect_agent_id: a.smart_connect_agent_id,
        agent_email: a.agent_email,
        agent_name: a.agent_name,
        departments: a.departments,
      })),
      user_id: userData?.id,
    };

    try {
      const result = await dispatch(
        createDynamicAgentGroupDispatcher(payload),
      ).unwrap();
      toast.success("Agent group created successfully 🎉");
      console.log("CREATE AGENT GROUP RESULT:", result);
      onSuccess(result?.data || result);
      onClose();
    } catch (error) {
      console.error("CREATE AGENT GROUP ERROR:", error);

      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message ||
            error?.response?.data?.message ||
            "Failed to create agent group. Please try again.";

      toast.error(errorMessage);
    }
  };

  const selectAllByDepartment = (department) => {
    if (!Array.isArray(agents) || agents.length === 0) return;
    console.log("agents agents : ", agents);

    const departmentAgents = agents.filter((a) =>
      a.departments.toLowerCase().includes(department.toLowerCase()),
    );

    if (departmentAgents.length === 0) {
      toast.info(`No ${department} agents found. Please Load ${department} Agents`);
      return;
    }

    setSelectedAgents((prev) => {
      const map = new Map(prev.map((a) => [a.id, a]));

      departmentAgents.forEach((a) => {
        map.set(a.id, a);
      });

      return Array.from(map.values());
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create New Agent Group</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <ProgressBar now={step === 1 ? 50 : 100} className="mb-3" />

        {step === 1 && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Agent Group Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
            </Form.Group>

            <div className="d-flex justify-content-between mb-2">
              <strong>Select Agents</strong>
              <small className="text-muted">
                Selected: {selectedAgents.length}
              </small>
            </div>

            <div className="d-flex gap-2 mb-3 flex-wrap">
              <Button
                size="sm"
                variant="outline-primary"
                disabled={loading}
                onClick={() => fetchAgents("CSM")}
              >
                Load CSM
              </Button>

              <Button
                size="sm"
                variant="outline-success"
                disabled={loading}
                onClick={() => fetchAgents("SALES_SETTLEMENT")}
              >
                Load Sales / Settlement
              </Button>

              <Button
                size="sm"
                variant="outline-warning"
                disabled={loading}
                onClick={() => fetchAgents("SALES")}
              >
                Load Sales
              </Button>
            </div>

            {/* SELECT ALL BUTTONS */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={loading || !agents.length}
                onClick={() => selectAllByDepartment("csm")}
              >
                Select All CSM
              </Button>

              <Button
                size="sm"
                variant="outline-secondary"
                disabled={loading || !agents.length}
                onClick={() => selectAllByDepartment("settlement")}
              >
                Select All Sales / Settlement
              </Button>

              <Button
                size="sm"
                variant="outline-secondary"
                disabled={loading || !agents.length}
                onClick={() => selectAllByDepartment("sales")}
              >
                Select All Sales
              </Button>
            </div>

            <div className="border rounded p-2" style={{ minHeight: 240 }}>
              {loading && (
                <div className="text-center my-4">
                  <Spinner animation="border" />
                </div>
              )}

              {!loading && agents.length > 0 && (
                <>
                  <InputGroup className="mb-2">
                    <Form.Control
                      placeholder="Search by name or email..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </InputGroup>

                  <ListGroup style={{ maxHeight: 180, overflowY: "auto" }}>
                    {filteredAgents.map((agent) => (
                      <ListGroup.Item key={agent.id}>
                        <Form.Check
                          type="checkbox"
                          checked={selectedAgents.some(
                            (a) => a.id === agent.id,
                          )}
                          onChange={() => toggleAgent(agent)}
                          label={
                            <>
                              <strong>{agent.agent_name}</strong>
                              <div className="text-muted small">
                                {agent.agent_email} • {agent.departments}
                              </div>
                            </>
                          }
                        />
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </>
              )}

              {!loading && agents.length === 0 && activeDepartment && (
                <div className="text-center text-muted mt-4">
                  No agents found
                </div>
              )}

              {!loading && agents.length === 0 && !activeDepartment && (
                <div className="text-center text-muted mt-4">
                  Please select a department to load agents
                </div>
              )}
            </div>

            {selectedAgents.length > 0 && (
              <div className="mt-2 d-flex flex-wrap gap-2">
                {selectedAgents.map((a) => (
                  <Badge
                    key={a.id}
                    pill
                    bg="secondary"
                    style={{ cursor: "pointer" }}
                    onClick={() => removeAgent(a.id)}
                  >
                    {a.agent_name} ✕
                  </Badge>
                ))}
              </div>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <Form.Group>
              <Form.Label>Smart Connect Setting</Form.Label>
              <Form.Select
                value={selectedSetting}
                disabled={smartConnectLoading}
                onChange={(e) => setSelectedSetting(e.target.value)}
              >
                <option value="">Select setting</option>
                {(Array.isArray(smartConnectSettings)
                  ? smartConnectSettings
                  : [smartConnectSettings]
                ).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.setting_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Button
              variant="link"
              className="p-0 mt-1"
              onClick={() => setShowCreateSettingModal(true)}
            >
              + Create New Setting
            </Button>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>

        <div className="d-flex gap-2">
          {step === 2 && (
            <Button variant="outline-secondary" onClick={() => setStep(1)}>
              Back
            </Button>
          )}

          {step === 1 && (
            <Button
              variant="primary"
              disabled={!groupName.trim() || !selectedAgents.length}
              onClick={() => setStep(2)}
            >
              Next
            </Button>
          )}

          {step === 2 && (
            <Button
              variant="primary"
              disabled={!selectedSetting}
              onClick={handleSubmit}
            >
              Create Group
            </Button>
          )}
        </div>

        <CreateSmartConnectSettingComponent
          show={showCreateSettingModal}
          onClose={() => setShowCreateSettingModal(false)}
          onSuccess={handleSettingCreated}
        />
      </Modal.Footer>
    </Modal>
  );
}

export default CreateNewAgentGroup;
